import {
  LightMode,
  LockOpenOutlined,
  LockOutlined,
  NightlightOutlined,
  SettingsOutlined,
} from "@mui/icons-material";
import { styled } from "@mui/material";
import React from "react";
import Clock from "src/assets/icons/Clock";
import TurboFanLogo from "src/assets/icons/TurboFanLogo";
import { MODE, modeToName } from "src/interfaces/IShadow";
import {
  LightStandardValues,
  SleepStandardValues,
} from "src/services/alen/alenDeviceConfig/utils";
import {
  ACTIVE_GREEN,
  BORDER_COLOR_BUTTON,
  INACTIVE_RED,
  WYND_WHITE,
} from "src/styles";

const Body = styled("span")({
  padding: "0 .5rem",
  lineHeight: "1rem",
  borderRadius: ".5rem",
  alignSelf: "center",
  transformOrigin: "top left",
  display: "flex",
});

const TextContainer = styled("span")({
  fontWeight: 900,
  fontSize: "0.5rem",
  whiteSpace: "nowrap",
});

interface WyndWhiteBadge {
  <Props extends { timeLeft?: string }>(
    toLabel: (props: Props) => string,
    icon?: React.ReactNode
  ): React.FC<Props>;
}

type WyndWhiteBadgeProps = {
  timeLeft?: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
};

// Use transform and absolute to workaround with browser's font rendering
const WyndWhiteBadge = ({
  timeLeft,
  label,
  icon,
  disabled = false,
  onClick,
}: WyndWhiteBadgeProps) => (
  <Body
    sx={{
      backgroundColor: disabled ? BORDER_COLOR_BUTTON : WYND_WHITE,
      gap: ".25rem",
      transition: "transform 0.3s",
      transformOrigin: "center",
      ":hover":
        onClick && !disabled
          ? {
              transform: "scale(1.1)",
              filter: "brightness(0.8)",
            }
          : {},
      cursor: disabled ? "not-allowed" : onClick ? "pointer" : "auto",
    }}
    onClick={!disabled ? onClick : undefined}
  >
    {icon || null}
    <TextContainer>
      {label}
      {timeLeft && (
        <>
          <Clock />
          {timeLeft}
        </>
      )}
    </TextContainer>
  </Body>
);

type LightProps = {
  on: boolean;
  timeLeft?: string;
};
export const Light = ({ on, ...rest }: LightProps) =>
  React.createElement(WyndWhiteBadge, {
    label: on ? "LIGHT ON" : "LIGHT OFF",
    ...rest,
  });

type LockedProps = {
  locked: boolean;
  timeLeft?: string;
  disabled?: boolean;
  onClick?: () => void;
};
export const Locked = ({ locked, ...rest }: LockedProps) =>
  React.createElement(WyndWhiteBadge, {
    label: locked ? "LOCKED" : "UNLOCKED",
    icon: locked ? (
      <LockOutlined sx={{ width: "1rem", height: "1rem" }} />
    ) : (
      <LockOpenOutlined sx={{ width: "1rem", height: "1rem" }} />
    ),
    ...rest,
  });

type AlenModeProps = {
  mode: "Auto" | "Manual" | "Sleep" | "N/A";
  timeLeft?: string;
  disabled?: boolean;
  onClick?: () => void;
};
export const AlenMode = ({ mode, ...rest }: AlenModeProps) =>
  React.createElement(WyndWhiteBadge, {
    label: mode.toUpperCase(),
    icon: <SettingsOutlined sx={{ width: "1rem", height: "1rem" }} />,
    ...rest,
  });

type AlenLightProps = {
  light: LightStandardValues;
  disabled?: boolean;
  onClick?: () => void;
};

export const AlenLight = ({ light, ...rest }: AlenLightProps) =>
  React.createElement(WyndWhiteBadge, {
    label: `LIGHT ${light}`,
    icon: <LightMode sx={{ width: "1rem", height: "1rem" }} />,
    ...rest,
  });

type AlenSleepProps = {
  sleep: SleepStandardValues;
  disabled?: boolean;
  onClick?: () => void;
};

export const AlenSleep = ({ sleep, ...rest }: AlenSleepProps) =>
  React.createElement(WyndWhiteBadge, {
    label: `SLEEP ${sleep}`,
    icon: <NightlightOutlined sx={{ width: "1rem", height: "1rem" }} />,
    ...rest,
  });

type GeneralProps = {
  labelText: string;
  icons?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
};

export const General = ({ labelText, icons = null, ...rest }: GeneralProps) =>
  React.createElement(WyndWhiteBadge, {
    label: labelText,
    icon: icons,
    ...rest,
  });

type SpeedProps = {
  speed: string;
  timeLeft?: string;
  disabled?: boolean;
  onClick?: () => void;
};

export const Speed = ({ speed, ...rest }: SpeedProps) =>
  React.createElement(WyndWhiteBadge, {
    label: `SPEED ${speed}`,
    icon: <TurboFanLogo sx={{ width: "1rem", height: "1rem" }} />,
    ...rest,
  });

type ModeProps = {
  mode: MODE;
  timeLeft?: string;
};

export const Mode = ({ mode, ...rest }: ModeProps) =>
  React.createElement(WyndWhiteBadge, {
    label: modeToName(mode).toUpperCase(),
    ...rest,
  });

export function PoweredOn({ onClick }: { onClick?: () => void }) {
  return (
    <Body
      onClick={onClick}
      sx={{
        backgroundColor: ACTIVE_GREEN,
        color: "white",
        transition: "transform 0.3s",
        transformOrigin: "center",
        ":hover": onClick
          ? {
              transform: "scale(1.1)",
              filter: "brightness(0.8)",
            }
          : {},
        cursor: onClick ? "pointer" : "auto",
      }}
    >
      <TextContainer>Powered On</TextContainer>
    </Body>
  );
}
export function PoweredOff({ onClick }: { onClick?: () => void }) {
  return (
    <Body
      onClick={onClick}
      sx={{
        backgroundColor: INACTIVE_RED,
        color: "white",
        cursor: onClick ? "pointer" : "auto",
      }}
    >
      <TextContainer>Powered Off</TextContainer>
    </Body>
  );
}

export function Online() {
  return (
    <Body sx={{ backgroundColor: ACTIVE_GREEN, color: "white" }}>
      <TextContainer>ONLINE</TextContainer>
    </Body>
  );
}

export function Offline() {
  return (
    <Body sx={{ backgroundColor: INACTIVE_RED, color: "white" }}>
      <TextContainer>OFFLINE</TextContainer>
    </Body>
  );
}
